import { Box, Button, Dialog, DialogContent, Grid, Typography } from "@mui/material";
import {
  BackArrow,
  breakpointsdata as BREAKPOINTS,
  getSingleCropBreakpoint,
  Loader,
} from "@platformx/utilities";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formGCBURL } from "../utils/cdnFlow";
import SelectedImageCrop from "./SelectedImageCrop";

type OriginalImagePayload = {
  original_image_relative_path?: string;
  ext?: string;
};

type CropVertexPoint = {
  x: number;
  y: number;
};

type CropVertex = CropVertexPoint[];

type PublishedImageCropHint = {
  aspect_ratio: string;
  crop_vertex: CropVertex;
  preview_url?: string;
};

// Empty placeholder so usages don't need a null check
const EMPTY_PUBLISHED_IMAGES: PublishedImageCropHint[] = [];

interface ManualCropUsingCdnProps {
  open: boolean;
  originalImage?: OriginalImagePayload;
  backTo: () => void;
  doneCropCompleted: (cropHints: PublishedImageCropHint[]) => void;
  isCrop?: boolean;
  published_images?: PublishedImageCropHint[];
  aspect_ratio?: string;
  prelemId?: string;
}

// Zero-size default used when there is no saved crop vertex for a breakpoint
const EMPTY_CROP_VERTEX: CropVertex = [
  { x: 0, y: 0 },
  { x: 0, y: 0 },
  { x: 0, y: 0 },
  { x: 0, y: 0 },
];

// Deep-clone a crop vertex array so mutations to the returned copy don’t affect the source
const cloneCropVertex = (cropVertex?: CropVertex): CropVertex => {
  if (!cropVertex?.length) {
    return EMPTY_CROP_VERTEX.map(({ x, y }) => ({ x, y }));
  }

  return cropVertex.map(({ x = 0, y = 0 }) => ({ x, y }));
};

/**
 * Builds the initial live-editing crop state from the saved published_images.
 * Each breakpoint is matched by its aspect_ratio name; unmatched ones get a zero-size vertex.
 * This runs once on mount and again each time the dialog reopens so stale edits are discarded.
 */
const getInitialCrops = (
  cropBreakpoints: Array<{ ratio: string }>,
  publishedImages?: PublishedImageCropHint[],
): CropVertex[] =>
  cropBreakpoints.map(({ ratio }) => {
    if (!publishedImages?.length) {
      return EMPTY_CROP_VERTEX.map(({ x, y }) => ({ x, y }));
    }
    const matchingCrop = publishedImages?.find(({ aspect_ratio }) => aspect_ratio === ratio) || {
      crop_vertex: EMPTY_CROP_VERTEX,
    };

    return cloneCropVertex(matchingCrop?.crop_vertex);
  });

const ManualCropUsingCdn = ({
  open,
  originalImage = {},
  backTo,
  doneCropCompleted,
  isCrop = true,
  published_images,
  aspect_ratio,
  prelemId,
}: ManualCropUsingCdnProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  // Normalise published_images input — prefer the prop, fall back to empty array.
  // Guard against non-array values (e.g. an object) so `.find`/`.[0]` stay safe.
  const publishedImages = Array.isArray(published_images)
    ? published_images
    : EMPTY_PUBLISHED_IMAGES;

  // Determine which breakpoints to show:
  //   isCrop=true  → all standard breakpoints (hero, landscape, card2, square, card1, portrait)
  //   isCrop=false → single breakpoint matching the aspect_ratio prop (for targeted crop dialogs)
  const cropBreakpoints = useMemo(
    () =>
      isCrop
        ? BREAKPOINTS
        : getSingleCropBreakpoint(aspect_ratio || publishedImages?.[0]?.aspect_ratio || "card2"),
    [isCrop, aspect_ratio, publishedImages],
  );
  // `initialSavedCrops` — stable snapshot from published_images, passed to croppers as their
  //   starting position. Only changes when published_images or breakpoints change (dialog reopen).
  // `crops`            — live editing state updated on every stencil drag, used for the Done payload.
  // Keeping them separate prevents the cropper from remounting while the user is dragging.
  const initialSavedCrops = useMemo(
    () => getInitialCrops(cropBreakpoints, publishedImages),
    [cropBreakpoints, publishedImages],
  );
  const [crops, setCrops] = useState<CropVertex[]>(
    getInitialCrops(cropBreakpoints, publishedImages),
  );

  const sourcePath = originalImage?.original_image_relative_path || "";
  const sourceExt = originalImage?.ext || "";
  const sourceImageUrl = formGCBURL(sourcePath, sourceExt);

  // Reset live crops to the saved state each time the dialog opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setCrops(initialSavedCrops);
    }
  }, [open, initialSavedCrops]);

  // Immutably update the crop for one breakpoint while leaving the others unchanged
  const onCropChange = (data: CropVertex, index: number) => {
    setCrops((previousCrops) => {
      const nextCrops = [...previousCrops];

      nextCrops[index] = data;
      return nextCrops;
    });
  };

  // On Done: assemble the crop hints array — each item stores the ratio name, the crop vertex
  // (pixel coordinates), and a CDN preview URL built from those vertices.
  const handleDone = () => {
    const cropHints = cropBreakpoints.map(({ ratio }, index) => ({
      aspect_ratio: ratio,
      crop_vertex: crops[index],
      //  preview_url: formManualCropPreviewFromGCBURL(sourcePath, crops[index], sourceExt),
    }));

    doneCropCompleted(cropHints);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={backTo}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'>
      <Grid
        container
        sx={{
          borderBottom: "1px solid #ced3d9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "10px 15px", md: "0px 24px" },
        }}>
        <Grid xs={12} md={8}>
          <BackArrow styles={{ paddingBottom: "10px" }} handleReturn={() => backTo()} />
          <Typography variant='h5semibold' component='span'>
            {t("crop_your_image")}
          </Typography>
        </Grid>
        <Grid
          xs={12}
          md={4}
          sx={{
            textAlign: { xs: "left", md: "right" },
            padding: { xs: "10px 0", md: "8px" },
          }}>
          <Button
            className='sm'
            variant='secondaryButton'
            onClick={() => backTo()}
            sx={{ marginRight: "12px" }}>
            {t("back")}
          </Button>
          <Button className='sm' variant='primaryButton' onClick={handleDone}>
            {t("done")}
          </Button>
        </Grid>
      </Grid>

      <DialogContent>
        <Box
          className='wholecontainer'
          sx={{
            background: { xs: "#f7f7f7", sm: "#fff" },
            padding: { xs: "11px", sm: "0px" },
          }}>
          {isLoading && <Loader />}

          <Grid container spacing={2}>
            {cropBreakpoints.map(({ aspectRatio, ratioName, aspectRatioName }, index) => {
              let ratioLabel = ratioName;
              if (isCrop || prelemId) {
                ratioLabel = `${ratioName}(${aspectRatioName})`;
              }
              // const cropPreviewUrl = formManualCropPreviewFromGCBURL(
              //   sourcePath,
              //   crops[index],
              //   sourceExt,
              // );
              // const hasCropBounds = Boolean(getCropBoundsFromVertices(crops[index]));

              return (
                <Grid xs={12} md={isCrop ? 6 : 12} em={isCrop ? 4 : 12} key={ratioName}>
                  <SelectedImageCrop
                    crop={`cdn-crop-${index + 1}`}
                    aspect={aspectRatio}
                    ratio={ratioLabel}
                    imageSrc={sourceImageUrl}
                    initialCrop={initialSavedCrops[index]}
                    onCropChange={(data: CropVertex) => onCropChange(data, index)}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                  />
                  {/* {hasCropBounds && (
                    <Box
                      sx={{
                        marginTop: "16px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "1px solid #ced3d9",
                        backgroundColor: "#fff",
                      }}>
                      <img
                        src={cropPreviewUrl}
                        alt={`${ratioName} crop preview`}
                        style={{
                          width: "100%",
                          maxHeight: "240px",
                          objectFit: "contain",
                          display: "block",
                          backgroundColor: "#f7f7f7",
                        }}
                      />
                      <Typography
                        variant='p2medium'
                        sx={{ padding: "12px 16px", textAlign: "center" }}>
                        {`CDN Preview: ${ratioName} (${aspectRatioName})`}
                      </Typography>
                    </Box>
                  )} */}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ManualCropUsingCdn;
